import {
  AgencyUuid,
  CaregiverUuid,
  ContractorRegularizationUuid,
  Gender,
  LegalMode,
  MonetBillUuid,
  MonetCoverageChangeEventUuid,
  MonetPaymentUuid,
  MonetProclientMemberFeeUuid,
  MonetProductUuid,
  PayslipXimiId,
  ProclientMemberUuid,
  ProclientUuid,
  RegularizationUuid,
  ThirdPartyCoverageUuid,
  ThirdPartyPayerUuid,
} from "hassibot/services_v2/common/types";
import {
  MonetBillInterventionComputed,
  MonetBillInterventionDeltaEnriched,
  MonetBillInterventionDigestComputed,
  MonetBillInterventionEnriched,
  MonetBillInterventionInput,
} from "hassibot/services_v2/monet/bills/types/interventions";
import {
  MonetBillDigestOhpProductsComputed,
  MonetBillDigestOhpProductsEnriched,
  MonetOhPComputed,
  MonetOhPEnriched,
  MonetOhPInput,
} from "hassibot/services_v2/monet/bills/types/ohp";
import {
  MonetBillPricingStrategyMandataryV1,
  MonetPricingStrategyMerged,
} from "hassibot/services_v2/monet/bills/types/pricing-strategy";
import {
  AmountInCents,
  CalendarSurchargeType,
  XimiProductType,
} from "hassibot/services_v2/monet/common/types";
import { MonetPaymentBreakdown } from "hassibot/services_v2/monet/payments/types";
import {
  MonetBillRegularizationEnriched,
  MonetBillRegularizationInput,
} from "hassibot/services_v2/monet/regularizations/types";
import { formatValueToDecimalString } from "hassibot/util/formatters";
import { DateTime } from "luxon";
import { ValueOf } from "types";

import { MonetBillSideEffects } from "./side-effects";

export const monetBillStatus = {
  DRAFT: "draft",
  WAITING_FOR_APPROVAL: "waiting_for_approval",
  WAITING_FOR_SENDING: "waiting_for_sending",
  SENDING_IN_PROGRESS: "sending_in_progress",
  SENDING_FAILURE: "sending_failure",
  SENT: "sent",
} as const;

export type MonetBillStatus = ValueOf<typeof monetBillStatus>;

export const LOCKED_BILL_STATUSES = [
  monetBillStatus.SENDING_IN_PROGRESS,
  monetBillStatus.SENDING_FAILURE,
  monetBillStatus.SENT,
];
export const isBillLocked = (billStatus: MonetBillStatus): boolean =>
  LOCKED_BILL_STATUSES.some(status => status === billStatus);

export enum CaregiverPayslipStatus {
  draft = "draft",
  sent = "sent",
}
export type CaregiverPayslip = {
  caregiverUuid: CaregiverUuid;
  caregiverGrossSalary: number;
  employerContributions: number;
  ximiId: PayslipXimiId;
  payDate: DateTime;
  reference: string;
  status: CaregiverPayslipStatus | null;
};
export enum MonetBillBreakdownStatus {
  paid = "paid",
  partiallyPaid = "partially_paid",
  unpaid = "unpaid",
}
type TransientMonetPaymentBreakdown = { monetBillUuid: MonetBillUuid; amountCents: number };

export enum MonetBillSource {
  ximi_v1 = "ximi",
  monet_v1 = "monet",
  ximi_v2 = "ximi_v2",
  monet_v2 = "monet_v2",
}

export enum MonetBillType {
  normalBill = "normal_bill",
  parentVirtualBill = "parent_virtual_bill",
  childBillProclient = "child_bill_proclient",
  childBillThirdParty = "child_bill_third_party",
}

export type SelectableMonetBill = {
  status: MonetBillStatus;
  // Nullable on the bill-labels variant of the endpoint: an aggregate whose bills
  // all carry no computed amount sums to null (absent/unknown), never a fake 0.
  totalAmountCents: number | null;
};

export type MonetBillsByUuid = {
  [id: MonetBillUuid]: SelectableMonetBill;
};

export const PROFORMA_REFERENCE_NUMBER = "PROFORMA";

export type MonetFeeInput = {
  uuid: MonetProclientMemberFeeUuid;
  product_uuid: MonetProductUuid;
  start_on: DateTime;
  end_on: DateTime | null;
};

export type MonetBillShort = {
  uuid: MonetBillUuid;
  agencyUuid: AgencyUuid;
  emittedOn: DateTime | null;
  mainPeriodStart: DateTime;
  mainPeriodEnd: DateTime;
  adjustmentsPeriodStart: DateTime;
  referenceNumber: string;
  proclientUuid: ProclientUuid;
  proclientMemberUuid: ProclientMemberUuid;
  proclientMemberFirstName: string | null;
  proclientMemberLastName: string | null;
  status: MonetBillStatus;
  issues: MonetBillIssues;
  paymentStatus: MonetBillBreakdownStatus | null;
  ximiId?: number;
  monetPaymentBreakdowns: TransientMonetPaymentBreakdown[];
  source: MonetBillSource;
  billType: MonetBillType;
  parentVirtualBillUuid: MonetBillUuid | null;
  thirdPartyPayerUuid: ThirdPartyPayerUuid | null;
  thirdPartyCoverageUuid: ThirdPartyCoverageUuid | null;
  totalAmountCents: number;
  totalUnpaidCents: number;
  marginRate: number | null;
  futurePaymentSideEffect: FuturePaymentSideEffectType;
};

export type CommonMonetBillInput = MonetBillShort & {
  userProvidedInput: MonetBillUserProvidedInput;
  sideEffectsRuns: MonetBillSideEffects[];
  sideEffectsLastRefresh: MonetBillSideEffects | null;
  updatedAt: DateTime | null;
};

export type MonetBillInputMandataryV1 = CommonMonetBillInput & {
  input: {
    pricingStrategies: MonetBillPricingStrategyMandataryV1[];
    relevantRegularizations: MonetBillRegularizationInput[];
    ohpProducts: MonetOhPInput;
    mainPeriodInterventions: MonetBillInterventionInput[];
    // On va comparer avec l'output de la précédente facture, d'où le "computed"
    adjustmentsPeriodCurrentInterventions: MonetBillInterventionInput[];
    adjustmentsPeriodPreviousInterventions: MonetBillInterventionComputed[];
    caregiversPayslips: CaregiverPayslip[];
    caregiversIdentities: MonetBillCaregiverIdentity[];
  };
};

// v2 input types
type CommonMonetBillInputV2 = CommonMonetBillInput & {
  input: {
    mainPeriodFees: MonetFeeInput[];
    pricingStrategiesMerged: MonetPricingStrategyMerged[];
    relevantRegularizations: MonetBillRegularizationInput[];
    mainPeriodInterventions: MonetBillInterventionInput[];
    // On va comparer avec l'output de la précédente facture, d'où le "computed"
    adjustmentsPeriodCurrentInterventions: MonetBillInterventionInput[];
    adjustmentsPeriodPreviousInterventions: MonetBillInterventionComputed[];
    caregiversIdentities: MonetBillCaregiverIdentity[];
  };
};

export type MonetBillInputContractorV2 = CommonMonetBillInputV2;
export type MonetBillInputMandataryV2 = CommonMonetBillInputV2 & {
  input: {
    caregiversPayslips: CaregiverPayslip[];
  };
};

export type MonetBillInputIssued = CommonMonetBillInput & {
  input: null;
};

export type MonetBillCaregiverIdentity = {
  [uuid: CaregiverUuid]: {
    caregiverLastName: string;
    caregiverFirstName: string;
    caregiverGender: Gender;
  };
};
export type MonetBillComputedContentMandataryV1 = {
  mainPeriodInterventions: MonetBillInterventionComputed[];
  adjustmentsPeriodCurrentInterventions: MonetBillInterventionComputed[];
  appliedRegularizations: MonetBillRegularizationInput[];
  adjustmentsPeriodInterventionDelta: MonetBillInterventionDigestComputed[];
  ohpProducts: MonetOhPComputed;
  totalCaregiversSuperGrossSalary: AmountInCents;
  digest: MonetBillDigestComputedMandataryV1;
  paymentBreakdowns: MonetPaymentBreakdown[];
};
type CommonMonetBillComputedContentV2 = {
  mainPeriodInterventions: MonetBillInterventionComputed[];
  adjustmentsPeriodCurrentInterventions: MonetBillInterventionComputed[];
  appliedRegularizations: MonetBillRegularizationInput[];
  adjustmentsPeriodInterventionDelta: MonetBillInterventionDigestComputed[];
  paymentBreakdowns: MonetPaymentBreakdown[];
};

export type MonetBillComputedContentContractorV2 = CommonMonetBillComputedContentV2 & {
  digest: MonetBillDigestComputedContractorV2;
};
export type MonetBillComputedContentMandataryV2 = CommonMonetBillComputedContentV2 & {
  digest: MonetBillDigestComputedMandataryV2;
  totalCaregiversSuperGrossSalary: AmountInCents;
};

export type MonetBillComputedMandataryV1 = MonetBillInputMandataryV1 & {
  computed: MonetBillComputedContentMandataryV1;
};
export type MonetBillComputedContractorV2 = MonetBillInputContractorV2 & {
  computed: MonetBillComputedContentContractorV2;
};
export type MonetBillComputedMandataryV2 = MonetBillInputMandataryV2 & {
  computed: MonetBillComputedContentMandataryV2;
};

type MonetXimiBillProduct = {
  calendarSurchargeType: CalendarSurchargeType | null;
  isNightSurcharged: boolean;
  pricingStrategyVersion: "XIMI";
  productName: string;
  productType: XimiProductType;
  quantity: number;
  totalAmountCents: number;
  unitAmountCents: number;
  vatRate: number;
};
export type MonetXimiBillComputedMandatary = MonetBillComputedMandataryV1 & {
  source: MonetBillSource.ximi_v1;
  computed: MonetBillComputedContentMandataryV1 & {
    digest: MonetBillDigestComputedMandataryV1 & { products: MonetXimiBillProduct[]; vat: number };
  };
  legalEntityMode: LegalMode;
};
export type MonetXimiBillComputedContractor = MonetBillComputedContractorV2 & {
  source: MonetBillSource.ximi_v2;
  computed: MonetBillComputedContentContractorV2 & {
    digest: MonetBillDigestComputedContractorV2 & {
      products: MonetXimiBillProduct[];
      adjustmentsPeriodProducts: MonetXimiBillProduct[];
      vat: number;
    };
  };
  legalEntityMode: LegalMode;
};
export type MonetXimiBillComputed =
  | MonetXimiBillComputedMandatary
  | MonetXimiBillComputedContractor;

export type MonetBillBreakable = {
  uuid: MonetBillUuid;
  mainPeriodStart: DateTime;
  mainPeriodEnd: DateTime;
  adjustmentsPeriodStart: DateTime;
  emittedAt: DateTime | null;
  referenceNumber: string;
  proclientMemberUuid: ProclientMemberUuid;
  proclientUuid: ProclientUuid;
  status: MonetBillStatus;
  source: MonetBillSource;
  checkedPaymentsUuids: MonetPaymentUuid[];
  totalAmountCents: AmountInCents;
  totalBreakdownCents: AmountInCents;
  totalUnpaidCents: AmountInCents;
  paymentBreakdowns: MonetPaymentBreakdown[];
  billType: MonetBillType;
};

export type MonetBillComputedIssued = MonetBillInputIssued & {
  computed: null;
};
export type MonetBillComputed =
  | MonetBillComputedMandataryV1
  | MonetBillComputedContractorV2
  | MonetBillComputedMandataryV2;

export type MaybeMonetBillComputed = MonetBillComputed | MonetBillComputedIssued;

export type MonetBillEnrichedMandataryV1 = MonetBillComputedMandataryV1 & {
  enriched: {
    mainPeriodInterventions: MonetBillInterventionEnriched[];
    adjustmentsPeriodInterventionsDelta: MonetBillInterventionDeltaEnriched[];
    relevantRegularizations: MonetBillRegularizationEnriched[];
    ohpProducts: MonetOhPEnriched[];
    appliedRegularizations: MonetBillRegularizationEnriched[];
    digest: MonetBillDigestEnrichedMandataryV1;
  };
  source: MonetBillSource.monet_v1 | MonetBillSource.ximi_v1;
  legalEntityMode: LegalMode.MANDATARY;
};
export type MonetBillEnrichedContractorV2 = MonetBillComputedContractorV2 & {
  enriched: {
    mainPeriodInterventions: MonetBillInterventionEnriched[];
    adjustmentsPeriodInterventionsDelta: MonetBillInterventionDeltaEnriched[];
    relevantRegularizations: MonetBillRegularizationEnriched[];
    appliedRegularizations: MonetBillRegularizationEnriched[];
    digest: MonetBillDigestEnrichedContractorV2;
  };
  source: MonetBillSource.monet_v2 | MonetBillSource.ximi_v2;
  legalEntityMode: LegalMode.CONTRACTOR;
};
export type MonetBillEnrichedMandataryV2 = MonetBillComputedMandataryV2 & {
  enriched: {
    mainPeriodInterventions: MonetBillInterventionEnriched[];
    adjustmentsPeriodInterventionsDelta: MonetBillInterventionDeltaEnriched[];
    relevantRegularizations: MonetBillRegularizationEnriched[];
    appliedRegularizations: MonetBillRegularizationEnriched[];
    digest: MonetBillDigestEnrichedMandataryV2;
  };
  source: MonetBillSource.monet_v2 | MonetBillSource.ximi_v2;
  legalEntityMode: LegalMode.MANDATARY;
};

export type MonetBillEnriched =
  | MonetBillEnrichedMandataryV1
  | MonetBillEnrichedMandataryV2
  | MonetBillEnrichedContractorV2;

export type MonetBillEnrichedIssued = MonetBillComputedIssued & {
  enriched: null;
  legalEntityMode: LegalMode;
};

export type MaybeMonetBillEnrichedMandatary =
  | MonetBillEnrichedMandataryV2
  | MonetBillEnrichedMandataryV1
  | MonetBillEnrichedIssued;

export type MaybeMonetBillEnrichedContractor =
  | MonetBillEnrichedContractorV2
  | MonetBillEnrichedIssued;

export type MaybeMonetBillEnrichedV1 = MonetBillEnrichedMandataryV1;
export type MaybeMonetBillEnrichedV2 = MonetBillEnrichedMandataryV2 | MonetBillEnrichedContractorV2;

export type MaybeMonetBillEnriched =
  | MaybeMonetBillEnrichedV2
  | MaybeMonetBillEnrichedV1
  | MonetBillEnrichedIssued;

export type MonetBillUserProvidedInput = {
  optedOutMainPeriodRegularizationsUuids: (ContractorRegularizationUuid | RegularizationUuid)[];
  optedInOtherPeriodsRegularizationsUuids: (ContractorRegularizationUuid | RegularizationUuid)[];
  adminApprovalLock: boolean; // This value is only used by the backend but it needs to be kept at this time in the frontend code base. I'll be passed to the worker after an interaction (like add or remove a regularization). This key isn't put to the backend after an interaction (only in the worker).
  // PEC change events the admin declared the regularizations were done for. As long as one
  // of the events at stake is missing here, the bill stays blocked.
  acknowledgedCoverageChangeEventUuids: MonetCoverageChangeEventUuid[];
};
type MonetBillDigestRegularizationComputed = {
  regularizationType: string;
  billAmountCents: number;
  totalAmountCents: number;
  category: string;
  quantity: number;
};
export type MonetBillDigestComputedMandataryV1 = {
  mainPeriodInterventions: MonetBillInterventionDigestComputed[];
  ohpProducts: MonetBillDigestOhpProductsComputed;
  adjustmentsPeriodCurrentInterventions: MonetBillInterventionDigestComputed[];
  regularizations: MonetBillDigestRegularizationComputed[];
  reducedVat: number;
  regularVat: number;
  totalAmountExcludingTaxCents: number;
  totalAmountCents: number;
  marginRate: number;
};

// V2 Digest types

type MonetBillDigestComputedStackElementV2 = {
  coverageUuid: ThirdPartyCoverageUuid | null;
  totalAmountCents: number;
  vatAmountCents: number;
  unitAmountInclVatMicros: number;
};

type MonetBillDigestInterventionComputedV2 = {
  pricingStrategyVersion: string;
  productUuid: string;
  productLabel: string;
  billingType: string;
  vatRate: number;
  nightSurchargeRate: number;
  isNightSurcharged: boolean;
  calendarSurchargeType: CalendarSurchargeType | null;
  calendarSurchargeRate: number;
  category: string;
  quantity: number;
  quantityType: string;
  unitAmountInclVatMicros: number;
  computedStackElements: MonetBillDigestComputedStackElementV2[];
};

type MonetBillDigestKilometricAllowanceComputedV2 = {
  category: string;
  pricingStrategyVersion: string;
  unitAmountCents: number;
  quantity: number;
  vatRate: number;
  computedStackElements: Omit<MonetBillDigestComputedStackElementV2, "unitAmountInclVatMicros">[];
};

type MonetBillDigestRegularizationComputedV2 = {
  regularizationType: string;
  category: string;
  quantity: number;
  vatRate: number;
  billLabel: string;
  computedStackElements: Omit<MonetBillDigestComputedStackElementV2, "unitAmountInclVatMicros">[];
};

type MonetBillDigestFeeProductComputedV2 = {
  pricingStrategyVersion: string;
  productUuid: string;
  category: string;
  vatRate: number;
  quantity: number;
  unitAmountInclVatMicros: number;
  computedStackElements: (MonetBillDigestComputedStackElementV2 & { productLabel: string })[];
};

export type ContractorMonetStackElementTotalComputed = {
  coverageUuid: ThirdPartyCoverageUuid | null;
  totalAmountCents: number;
  totalAmountExclTaxCents: number;
  vatAmountCentsByRate: VatAmountsByRate;
};

export type MandataryMonetStackElementTotalComputed = ContractorMonetStackElementTotalComputed & {
  caregiversNetSalaryAmountCents: number;
  caregiversSocialChargesAmountCents: number;
  caregiversTaxDeducedAtSourceAmountCents: number;
  managementFeesExclTaxCentsByRate: VatAmountsByRate;
  managementFeesVatQuotaByRate: Record<number, number>;
  marginRate: number;
};

type CommonMonetBillDigestComputedV2 = {
  mainPeriodInterventions: MonetBillDigestInterventionComputedV2[];
  adjustmentsPeriodInterventions: MonetBillDigestInterventionComputedV2[];
  regularizations: MonetBillDigestRegularizationComputedV2[];
  kilometricAllowance: MonetBillDigestKilometricAllowanceComputedV2[];
  adjustmentsKilometricAllowance: MonetBillDigestKilometricAllowanceComputedV2[];
  feeProducts: MonetBillDigestFeeProductComputedV2[];
};

export type MonetBillDigestComputedContractorV2 = CommonMonetBillDigestComputedV2 & {
  stackElementTotals: ContractorMonetStackElementTotalComputed[];
};

export type MonetBillDigestComputedMandataryV2 = CommonMonetBillDigestComputedV2 & {
  marginRate: number;
  stackElementTotals: MandataryMonetStackElementTotalComputed[];
};

type MonetBillDigestProductEnrichedMandataryV1 = {
  productLabel: string;
  durationLabel: string;
  startAtLabel: string;
  amountLabel: string;
  categoryLabel: string;
  quantityLabel: string;
  unitAmountLabel: string;
  totalAmountLabel: string;
};

export type MonetBillDigestEnrichedMandataryV1 = {
  mainPeriodInterventions: MonetBillDigestProductEnrichedMandataryV1[];
  adjustmentsPeriodInterventions: MonetBillDigestProductEnrichedMandataryV1[];
  regularizations: MonetBillDigestProductEnrichedMandataryV1[];
  kilometricAllowance: MonetBillDigestProductEnrichedMandataryV1[];
  adjustmentsKilometricAllowance: MonetBillDigestProductEnrichedMandataryV1[];
  ohpProducts: MonetBillDigestOhpProductsEnriched[];
  regularVatLabel: string;
  reducedVatLabel: string;
  totalAmountExcludingTaxLabel: string;
  totalAmountLabel: string;
};

// V2 Enriched types

type MonetBillDigestProductEnrichedV2 = {
  productLabel: string;
  categoryLabel: string;
  quantityLabel: string;
  unitAmountLabel: string;
  totalAmountLabel: string;
  vatRateLabel: string;
};

export type CommonMonetBillDigestEnrichedV2 = {
  mainPeriodInterventions: MonetBillDigestProductEnrichedV2[];
  adjustmentsPeriodInterventions: MonetBillDigestProductEnrichedV2[];
  regularizations: MonetBillDigestProductEnrichedV2[];
  kilometricAllowance: MonetBillDigestProductEnrichedV2[];
  adjustmentsKilometricAllowance: MonetBillDigestProductEnrichedV2[];
  feeProducts: MonetBillDigestProductEnrichedV2[];
  manualProducts: MonetBillDigestProductEnrichedV2[];
  vatLabelByRate: VatLabelsByRate;
  totalVatLabel: string;
  totalAmountExclTaxLabel: string;
  totalAmountLabel: string;
};

export type MonetBillDigestEnrichedContractorV2 = CommonMonetBillDigestEnrichedV2;

export type MonetBillDigestEnrichedMandataryV2 = CommonMonetBillDigestEnrichedV2 & {
  managementFeesExclTaxLabel: string;
};

export type MonetBillIssue = { type: string; extra: Record<string, string> | null };
export enum MonetBillIssueCategory {
  errors = "errors",
  warnings = "warnings",
  blockings = "blockings",
}
export type MonetBillIssues = {
  [MonetBillIssueCategory.errors]: MonetBillIssue[];
  [MonetBillIssueCategory.warnings]: MonetBillIssue[];
  [MonetBillIssueCategory.blockings]: MonetBillIssue[];
};

export const monetBillShortHasErrorIssued = (monetBillShort: MonetBillShort): boolean =>
  monetBillShort.issues[MonetBillIssueCategory.errors].length > 0;
export const isMonetBillComputed = (
  monetBillComputed: MaybeMonetBillComputed
): monetBillComputed is MonetBillComputed =>
  monetBillComputed.issues[MonetBillIssueCategory.errors].length === 0;
export const monetBillEnrichedHasErrorIssued = (
  monetBillEnriched: MaybeMonetBillEnriched
): monetBillEnriched is MonetBillEnrichedIssued =>
  monetBillEnriched.issues[MonetBillIssueCategory.errors].length > 0;

export const monetBillHasIssues = (monetBillEnriched: MaybeMonetBillEnriched): boolean =>
  Object.values(monetBillEnriched).flat().length > 0;

export const REGULAR_VAT = "regular_vat";
export const REDUCED_VAT = "reduced_vat";

export const REGULARIZATION_VAT_RATE = {
  regular_vat: "20%",
  reduced_vat: "5,5%",
};

export enum FuturePaymentSideEffectType {
  NO_FUTURE_PAYMENT = "no_future_payment",
  PAYMENT_INTENT_ALREADY_CREATED = "payment_intent_already_created",
  TAX_CREDIT_PAYMENT_REQUEST_ALREADY_CREATED = "tax_credit_payment_request_already_created",
  PAYMENT_INTENT_WILL_BE_CREATED = "payment_intent_will_be_created",
  TAX_CREDIT_PAYMENT_REQUEST_WILL_BE_CREATED = "tax_credit_payment_request_will_be_created",
  MISSING_CESU_EXCLUSION = "missing_cesu_exclusion",
  OTHER_EXCLUSION = "other_exclusion",
}

export const futurePaymentSideEffectTypesForPaidBills = [
  FuturePaymentSideEffectType.NO_FUTURE_PAYMENT,
  FuturePaymentSideEffectType.PAYMENT_INTENT_ALREADY_CREATED,
  FuturePaymentSideEffectType.TAX_CREDIT_PAYMENT_REQUEST_ALREADY_CREATED,
];

export type VatAmountsByRate = Record<number, number>;
export type VatLabelsByRate = Record<number, string>;

export const VatRate = {
  exempt: 0,
  reduced: 550,
  intermediate: 1000,
  regular: 2000,

  values: (): number[] => {
    return [VatRate.exempt, VatRate.reduced, VatRate.intermediate, VatRate.regular];
  },

  ratioToFloat: () => 10000,

  /**
   * Convert a VatType to its percentage value as a float ratio
   * For example, 'reduced' (5.5%) becomes 0.055
   */
  toFloatRatio: (vatRate: number): number => {
    return vatRate / VatRate.ratioToFloat();
  },

  /**
   * Convert a VatType to its percentage value as a number
   * For example, 'reduced' becomes 5.5, 'regular' becomes 20
   */
  toPercent: (vatRate: number): number => {
    return vatRate / 100;
  },

  /**
   * Convert a VatType to its percentage value as a formatted string
   * For example, 'reduced' becomes "5.5%", 'regular' becomes "20%"
   */
  toPercentString: (vatRate: number): string => {
    return `${formatValueToDecimalString(VatRate.toPercent(vatRate))} %`;
  },
} as const;

export type MonetBillBatchResponse = {
  batchUuid: string;
  numberOfCreatedJobs: number;
};
